import { type ReactNode, useState } from 'react';
import { useEffect } from 'react';
import clsx from 'clsx';
import { type GridApi, type IRowNode } from '@ag-grid-community/core';
import { Icon } from '@ics-portal/guardian';
import { type CustomCellConfig } from './CustomCellConfig';
import s from './CustomCell.module.scss';

function ExpandToggle<T>({
    node,
    api,
}: {
    node: IRowNode<T>;
    api: GridApi<T>;
}): ReactNode {
    // keep state in sync with node.expanded to render the correct icon
    const [expanded, setExpanded] = useState<boolean>(node.expanded);

    useEffect(() => {
        setExpanded(node.expanded);
    }, [node.expanded]);

    const className = clsx(s.triggerIcon, expanded ? s.expanded : null);

    return (
        <div
            style={{ display: 'flex', alignItems: 'center' }}
            ref={(ref) => {
                if (!ref) return;
                ref.onclick = (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const expanded = !node.expanded;
                    api.forEachNode((n) => {
                        api.setRowNodeExpanded(n, false);
                    });
                    api.setRowNodeExpanded(node, expanded);
                };
            }}
        >
            <Icon className={className} name="chevron_right" />
        </div>
    );
}

export function CustomCell<T>(config: CustomCellConfig<T>): ReactNode {
    const { params, counter, label, expandable, pinnedRowRenderer } = config;

    if (pinnedRowRenderer && params.node.rowPinned) {
        return pinnedRowRenderer(params);
    }

    const hasCounter = typeof counter === 'number';

    return (
        <div className="flex gap-4 justify-content-start align-items-center">
            {expandable || hasCounter ? (
                <div className="flex gap-1 justify-content-center align-items-center">
                    {expandable ? (
                        <ExpandToggle node={params.node} api={params.api} />
                    ) : null}
                    {hasCounter ? (
                        <span className={s.counter}>{counter}</span>
                    ) : null}
                </div>
            ) : null}
            {label}
        </div>
    );
}
